import type { Survey, Question, MultilingualText } from '../components/survey/survey.types';

/**
 * New Survey API Response Types
 */
interface NewSurveyOption {
  id: number;
  label: string;
}

interface NewSubQuestion {
  id: number;
  title: string;
}

interface NewQuestionDetail {
  question_id: number;
  language_code: string;
  title: string;
  options: NewSurveyOption[] | null;
  sub_questions?: NewSubQuestion[] | null;
}

interface NewQuestion {
  question_id: number;
  type:
    | 'options'
    | 'free_text'
    | 'multi_line_text'
    | 'single_line_text'
    | 'rating'
    | 'matrix'
    | 'number_rating'
    | 'email'
    | 'dropdown';
  required: boolean;
  minSelectable: number | null;
  maxSelectable: number | null;
  showOther?: boolean;
  otherValue?: APIMultilingualMessage[];
  shuffleOptions?: boolean;
  details: NewQuestionDetail[];
}

interface NewPage {
  page: number;
  paginateQuestions: boolean;
  questions: NewQuestion[];
}

/**
 * Represents a multilingual text message from the API
 * Used for various fields like thank you messages, titles, descriptions, etc.
 */
interface APIMultilingualMessage {
  language_code: string;
  text: string;
}

interface NewSurvey {
  survey_id: string;
  projectJira: string;
  surveyName: string;
  surveyBrand: string;
  languages: string[];
  pages: NewPage[];
  state: string;
  publishedAt: string;
  modifiedAt: string;
  surveyLink: string;
  header?: 'banner' | 'full' | null;
  progressBar?: boolean;
  thankYou?: APIMultilingualMessage[];
  surveyEnd?: string | null;
  surveyTitle?: APIMultilingualMessage[];
  surveyDescription?: APIMultilingualMessage[];
  onePageOnMobile?: boolean;
  oneResponsePerPerson?: boolean;
}

/**
 * Converts new API survey format to the existing internal format
 * This allows us to keep all existing components working without changes
 */
export function adaptSurveyFromAPI(newSurvey: NewSurvey): Survey {
  const headerMode = newSurvey.header ?? null;
  const thankYouMessage = adaptMultilingualMessages(newSurvey.thankYou);
  const surveyTitle = adaptMultilingualMessages(newSurvey.surveyTitle);
  const surveyDescription = adaptMultilingualMessages(newSurvey.surveyDescription);

  return {
    name: newSurvey.surveyName,
    client: newSurvey.surveyBrand,
    description: '', // Not provided in new format
    languages: newSurvey.languages.map(lang => lang.toLowerCase()),
    // For legacy use, treat any non-null header as "show some header"
    show_header_banner: headerMode ? true : false,
    header_mode: headerMode,
    show_progress_bar: newSurvey.progressBar ?? true,
    survey_end: newSurvey.surveyEnd ?? null,
    thank_you_message: thankYouMessage,
    survey_title: surveyTitle,
    survey_description: surveyDescription,
    one_page_on_mobile: newSurvey.onePageOnMobile ?? false,
    one_response_per_person: newSurvey.oneResponsePerPerson ?? false,
    pages: newSurvey.pages.map(page => ({
      page_number: page.page,
      show_questions_per_page: page.paginateQuestions,
      questions: page.questions.map(q => adaptQuestion(q, newSurvey.languages))
    }))
  };
}

/**
 * Converts a single question from new format to old format
 */
function adaptQuestion(newQuestion: NewQuestion, _languages: string[]): Question {
  const questionText = createMultilingualText(newQuestion.details, 'title');

  // Map question type
  const questionType = mapQuestionType(newQuestion.type);

  // Base question properties
  const baseQuestion = {
    question_number: newQuestion.question_id,
    question_type: questionType,
    question_text: questionText,
    is_required: newQuestion.required
  };

  // Handle different question types
  switch (newQuestion.type) {
    case 'options':
      return {
        ...baseQuestion,
        question_type: 'multiple_choice' as const,
        choices: adaptChoices(newQuestion.details, _languages),
        showOther: newQuestion.showOther ?? false,
        otherValue: adaptMultilingualMessages(newQuestion.otherValue),
        minSelectable: newQuestion.minSelectable,
        maxSelectable: newQuestion.maxSelectable,
        shuffleOptions: newQuestion.shuffleOptions ?? false
      };

    case 'dropdown':
      return {
        ...baseQuestion,
        question_type: 'dropdown' as const,
        choices: adaptChoices(newQuestion.details, _languages),
        showOther: newQuestion.showOther ?? false,
        otherValue: adaptMultilingualMessages(newQuestion.otherValue)
      };

    case 'free_text':
    case 'multi_line_text':
      return {
        ...baseQuestion,
        question_type: 'multi_line_text' as const
      };

    case 'single_line_text':
      return {
        ...baseQuestion,
        question_type: 'single_line_text' as const
      };

    case 'email':
      return {
        ...baseQuestion,
        question_type: 'email' as const
      };

    case 'rating':
      // Default to star rating, could be enhanced to detect number rating
      return {
        ...baseQuestion,
        question_type: 'star_rating' as const,
        maxSelectable: newQuestion.maxSelectable
      };

    case 'number_rating': {
      // Number rating questions: use min/maxSelectable as the numeric range
      // and the first and last options as the endpoint labels (min/max)
      const min = newQuestion.minSelectable ?? 0;
      const max = newQuestion.maxSelectable ?? 10;

      // Get first detail to access options
      const firstDetail = newQuestion.details[0];
      const options = firstDetail?.options || [];

      // Endpoint labels: first option = min label, last option = max label
      const firstOptionId = options.length > 0 ? options[0].id : 1;
      const lastOptionId = options.length > 0 ? options[options.length - 1].id : 2;

      const min_label = createChoiceText(newQuestion.details, firstOptionId);
      const max_label = createChoiceText(newQuestion.details, lastOptionId);

      return {
        ...baseQuestion,
        question_type: 'number_rating' as const,
        scale: {
          min,
          min_label,
          max,
          max_label,
        },
      };
    }

    case 'matrix':
      // Matrix questions: sub_questions become the sub-questions (rows)
      // and options become the answer choices (columns)
      const matrixSubQuestions = adaptMatrixSubQuestions(newQuestion.details);
      const matrixAnswers = adaptMatrixAnswers(newQuestion.details);
      const finalMatrixAnswers =
        matrixAnswers.length > 0
          ? matrixAnswers
          : getDefaultMatrixAnswers(newQuestion.details);
      return {
        ...baseQuestion,
        question_type: 'matrix' as const,
        answers: finalMatrixAnswers,
        questions: matrixSubQuestions
      };

    default:
      // Fallback to single line text
      return {
        ...baseQuestion,
        question_type: 'single_line_text' as const
      };
  }
}

/**
 * Maps new question types to old question types
 */
function mapQuestionType(newType: string): string {
  const typeMap: Record<string, string> = {
    'options': 'multiple_choice',
    'free_text': 'multi_line_text',
    'multi_line_text': 'multi_line_text',
    'single_line_text': 'single_line_text',
    'rating': 'star_rating',
    'matrix': 'matrix',
    'number_rating': 'number_rating',
    'email': 'email',
    'dropdown': 'dropdown'
  };
  return typeMap[newType] || 'single_line_text';
}

/**
 * Creates multilingual text object from question details
 */
function createMultilingualText(
  details: NewQuestionDetail[],
  field: 'title'
): MultilingualText {
  const multilingualText: MultilingualText = {
    en: '',
    fr: ''
  };

  details.forEach(detail => {
    const langCode = detail.language_code.toLowerCase();
    multilingualText[langCode] = detail[field];
  });

  // Ensure we have at least English
  if (!multilingualText.en && details.length > 0) {
    multilingualText.en = details[0][field];
  }

  return multilingualText;
}

/**
 * Adapts choices from new format to old format
 */
function adaptChoices(details: NewQuestionDetail[], _languages: string[]) {
  // Get options from the first detail (assuming all have same options)
  const firstDetail = details[0];
  if (!firstDetail.options) {
    return [];
  }

  return firstDetail.options.map(option => ({
    choice_number: option.id,
    choice_text: createChoiceText(details, option.id)
  }));
}

/**
 * Creates multilingual choice text
 */
function createChoiceText(
  details: NewQuestionDetail[],
  optionId: number
): MultilingualText {
  const multilingualText: MultilingualText = {
    en: '',
    fr: ''
  };

  details.forEach(detail => {
    const langCode = detail.language_code.toLowerCase();
    const option = detail.options?.find(opt => opt.id === optionId);
    if (option) {
      multilingualText[langCode] = option.label;
    }
  });

  return multilingualText;
}

/**
 * Adapts multilingual text messages from the new format array into multilingual text
 * Used for thank-you messages, survey titles, descriptions, and other multilingual fields
 */
function adaptMultilingualMessages(messages?: APIMultilingualMessage[]): MultilingualText | null {
  if (!messages || !Array.isArray(messages) || messages.length === 0) {
    return null;
  }

  const multilingualText: MultilingualText = {
    en: '',
    fr: ''
  };

  messages.forEach(msg => {
    const langCodeRaw = msg.language_code;
    if (!langCodeRaw) return;
    const langCode = langCodeRaw.toLowerCase();
    multilingualText[langCode] = msg.text;
  });

  // Ensure we have at least English fallback
  if (!multilingualText.en) {
    multilingualText.en = messages[0].text;
  }

  return multilingualText;
}

/**
 * Creates multilingual sub-question text (from sub_questions)
 */
function createSubQuestionText(
  details: NewQuestionDetail[],
  subQuestionId: number
): MultilingualText {
  const multilingualText: MultilingualText = {
    en: '',
    fr: ''
  };

  details.forEach(detail => {
    const langCode = detail.language_code.toLowerCase();
    const subQuestion = detail.sub_questions?.find(sq => sq.id === subQuestionId);
    if (subQuestion) {
      multilingualText[langCode] = subQuestion.title;
    }
  });

  return multilingualText;
}

/**
 * Adapts matrix sub-questions from new format sub_questions
 */
function adaptMatrixSubQuestions(details: NewQuestionDetail[]) {
  const firstDetail = details[0];
  const hasSubQuestions = Array.isArray(firstDetail.sub_questions) && firstDetail.sub_questions.length > 0;
  const hasOptions = Array.isArray(firstDetail.options) && firstDetail.options.length > 0;

  // New format: sub_questions are the rows
  if (hasSubQuestions) {
    return (firstDetail.sub_questions || []).map(subQuestion => ({
      question_number: subQuestion.id,
      question_text: createSubQuestionText(details, subQuestion.id)
    }));
  }

  // Backwards compatibility: if no sub_questions field, fall back to using options as rows
  if (hasOptions) {
    return (firstDetail.options || []).map(option => ({
      question_number: option.id,
      question_text: createChoiceText(details, option.id)
    }));
  }

  return [];
}

/**
 * Adapts matrix answers (columns) from new format options
 */
function adaptMatrixAnswers(details: NewQuestionDetail[]) {
  const firstDetail = details[0];
  const hasSubQuestions = Array.isArray(firstDetail.sub_questions) && firstDetail.sub_questions.length > 0;
  const hasOptions = Array.isArray(firstDetail.options) && firstDetail.options.length > 0;

  // Only use options as columns when we are in the new format
  // where sub_questions provide the rows and options provide the column labels
  if (hasSubQuestions && hasOptions) {
    return (firstDetail.options || []).map(option => ({
      answer_number: option.id,
      answer_text: createChoiceText(details, option.id)
    }));
  }

  // Otherwise, fall back to default Likert scale handled by getDefaultMatrixAnswers
  return [];
}

/**
 * Returns default answer scale for matrix questions
 * Uses a 5-point Likert scale
 */
function getDefaultMatrixAnswers(details: NewQuestionDetail[]) {
  const hasEnglish = details.some(d => d.language_code.toLowerCase() === 'en');
  const hasFrench = details.some(d => d.language_code.toLowerCase() === 'fr');

  return [
    {
      answer_number: 1,
      answer_text: {
        en: hasEnglish ? 'Strongly Agree' : '',
        fr: hasFrench ? 'Tout à fait d\'accord' : ''
      }
    },
    {
      answer_number: 2,
      answer_text: {
        en: hasEnglish ? 'Agree' : '',
        fr: hasFrench ? 'D\'accord' : ''
      }
    },
    {
      answer_number: 3,
      answer_text: {
        en: hasEnglish ? 'Neither Agree nor Disagree' : '',
        fr: hasFrench ? 'Ni d\'accord ni en désaccord' : ''
      }
    },
    {
      answer_number: 4,
      answer_text: {
        en: hasEnglish ? 'Disagree' : '',
        fr: hasFrench ? 'Pas d\'accord' : ''
      }
    },
    {
      answer_number: 5,
      answer_text: {
        en: hasEnglish ? 'Strongly Disagree' : '',
        fr: hasFrench ? 'Pas du tout d\'accord' : ''
      }
    }
  ];
}
